import { Component } from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import { graphql, createRefetchContainer } from 'react-relay/legacy';
import { trackBannerAdsAbTestVariant } from 'dibs-google-ad-manager/exports/abTestHelpers';
import { RecentlyViewedItemsProvider } from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';
import { ServerVarsContext } from '../global/ServerVarsContext/ServerVarsContext';

// components
import { PdpRespCondensedContainer } from './PdpRespCondensedContainer/PdpRespCondensedContainer';
import { PdpSharedLayout } from './PdpSharedLayout';
import { PdpSharedAuctionContextProvider } from './PdpSharedAuctionContext/PdpSharedAuctionContext';

export class PdpRespCondensedLayoutComponent extends Component {
    constructor(props) {
        super(props);

        this.getAdditionalTrackingData = this.getAdditionalTrackingData.bind(this);
    }

    componentDidMount() {
        trackBannerAdsAbTestVariant();
    }

    getAdditionalTrackingData() {
        return {
            pageType: 'Products',
            isCondensed: 'Y',
        };
    }

    render() {
        const viewer = this.props?.viewer || null;
        const user = viewer?.user || null;
        const item = viewer?.itemRead?.[0] || null;
        const similarLandingItems = viewer.similarLandingItems || null;

        return (
            <PdpSharedLayout
                user={user}
                viewer={viewer}
                additionalTrackingData={this.getAdditionalTrackingData()}
                doRefetch={this.props.relay.refetch}
            >
                {({ pdpHead, currency, selectedSkuId }) => (
                    <>
                        <Helmet {...pdpHead.getCondensedPdpData()} />
                        <RecentlyViewedItemsProvider
                            excludedItemIds={item?.serviceId ? [item?.serviceId] : null}
                            maxItems={this.context.isTablet ? 10 : 12}
                            itemsPerPage={this.context.isTablet ? 5 : 6}
                        >
                            <PdpSharedAuctionContextProvider>
                                <PdpRespCondensedContainer
                                    currency={currency}
                                    selectedSkuId={selectedSkuId}
                                    user={user}
                                    item={item}
                                    viewer={viewer}
                                    similarLandingItems={similarLandingItems}
                                />
                            </PdpSharedAuctionContextProvider>
                        </RecentlyViewedItemsProvider>
                    </>
                )}
            </PdpSharedLayout>
        );
    }
}

PdpRespCondensedLayoutComponent.propTypes = {
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
};

PdpRespCondensedLayoutComponent.contextType = ServerVarsContext;

export const PdpRespCondensedLayout = createRefetchContainer(
    PdpRespCondensedLayoutComponent,
    {
        viewer: graphql`
            fragment PdpRespCondensedLayout_viewer on Viewer {
                ...PdpRespCondensedContainer_viewer
                ...PdpSharedLayout_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    ...PdpRespCondensedContainer_user
                    ...PdpSharedLayout_user
                }
                itemRead(itemIds: $itemIds) {
                    ... on Item {
                        serviceId
                        ...PdpRespCondensedContainer_item
                    }
                }
                similarLandingItems: moreLikeThisItem(
                    first: 12
                    itemId: $itemId
                    isTrade: $isTrade
                    onlyAuctionItems: true
                    useML: true
                    mlType: $mlType
                ) @include(if: $fetchPdp) {
                    ...PdpRespCondensedContainer_similarLandingItems
                }
            }
        `,
    },
    graphql`
        query PdpRespCondensedLayoutRefetchQuery(
            $isClient: Boolean!
            $afterDisplayOrBuaSsr: Boolean!
            $afterDisplay: Boolean!
            $itemId: String!
            $itemIds: [String]!
            $userId: String = ""
            $userIds: [String]
            $fetchUser: Boolean!
            $isTrade: Boolean!
            $isMultiSku: Boolean = false
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
            $fetchPdp: Boolean!
            $forcedCurrency: Currency
            $localRecentHistoryItemIds: [String] = []
            $fetchUserRecentHistoryItem: Boolean = false
            $userCountryCode: String = ""
            $priceBookName: String = ""
            $isInteractivePdpChat: Boolean = false
            $fetchRegionalInfo: Boolean = false
            $userZipCode: String!
            $fetchAuction: Boolean = false
            $mlType: MlType
        ) {
            viewer {
                ...PdpRespCondensedLayout_viewer
            }
        }
    `
);
